import { useState, useEffect, useCallback } from 'react';
import API from '@/utils/api';

/**
 * usePushNotifications - Hook for Web Push notification management
 * 
 * Features:
 * - Register service worker
 * - Subscribe to push notifications
 * - Handle permission requests
 * - Manage subscription status
 */
const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      const supported = 
        'serviceWorker' in navigator &&
        navigator.serviceWorker &&
        'PushManager' in window &&
        'Notification' in window;
      
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
      }
    };
    
    checkSupport();
  }, []);

  // Register service worker and check subscription status
  useEffect(() => {
    if (!isSupported) {
      setLoading(false);
      return;
    }

    const init = async () => {
      try {
        // Register service worker
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registered:', registration.scope);

        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;

        // Check current subscription
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (err) {
        console.error('Push notification init error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [isSupported]);

  /**
   * Convert VAPID key from base64 to Uint8Array
   */
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  /**
   * Subscribe to push notifications
   */
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      throw new Error('Push notifications not supported');
    }

    setLoading(true);
    setError(null);

    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        throw new Error('Permission denied');
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key from server
      const { data: vapidData } = await API.get('/notifications/vapid-public-key');
      const applicationServerKey = urlBase64ToUint8Array(vapidData.publicKey);

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // Send subscription to server
      const subscriptionJSON = subscription.toJSON();
      await API.post('/notifications/subscribe', {
        endpoint: subscriptionJSON.endpoint,
        keys: subscriptionJSON.keys,
      });

      setIsSubscribed(true);
      return { success: true };
    } catch (err) {
      console.error('Subscribe error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;

    setLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
      }

      // Notify server
      await API.delete('/notifications/unsubscribe');

      setIsSubscribed(false);
      return { success: true };
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  /**
   * Request notification permission
   */
  const requestPermission = useCallback(async () => {
    if (!isSupported) return 'denied';

    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, [isSupported]);

  /**
   * Show a local notification (for testing)
   */
  const showLocalNotification = useCallback(async (title, options = {}) => {
    if (!isSupported || permission !== 'granted') {
      console.warn('Cannot show notification - not supported or not permitted');
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    
    await registration.showNotification(title, {
      body: options.body || '',
      icon: options.icon || '/icon-192.png',
      badge: options.badge || '/badge-72.png',
      tag: options.tag || 'default',
      data: options.data || {},
      requireInteraction: options.requireInteraction || false,
      actions: options.actions || [],
      vibrate: [200, 100, 200],
    });
  }, [isSupported, permission]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    requestPermission,
    showLocalNotification,
  };
};

export default usePushNotifications;
